import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  Activity,
  ChevronDown,
  Flag,
  LayoutDashboard,
  LogOut,
  Search,
  Settings,
  ShieldCheck,
  UserCog,
  Users,
} from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Logo } from '@/components/shared/logo';
import { canAccessAdminArea, type AdminAreaAccess } from '@/features/admin/access';
import { ThemeToggle } from '@/components/shared/theme-toggle';
import { ROUTES } from '@/lib/constants';
import { cn } from '@/lib/utils';
import { useAdmin, useAuthStore } from '@/stores/auth-store';

type NavItem = AdminAreaAccess & {
  to: string;
  icon: typeof LayoutDashboard;
  label: string;
};

const nav: NavItem[] = [
  { to: ROUTES.admin, icon: LayoutDashboard, label: 'Overview' },
  { to: '/admin/verifications', icon: ShieldCheck, label: 'Verifications', module: 'verification' },
  { to: ROUTES.adminUsers, icon: Users, label: 'Users', module: 'user-database-search' },
  { to: ROUTES.adminManagement, icon: UserCog, label: 'Admins', module: 'admin-user-management' },
  { to: ROUTES.adminReports, icon: Flag, label: 'Reports' },
  { to: '/admin/audit', icon: Activity, label: 'Audit logs', module: 'audit-logs' },
  { to: ROUTES.adminSettings, icon: Settings, label: 'Settings', superOnly: true },
];

export function AdminShell() {
  const admin = useAdmin();
  const signOutAdmin = useAuthStore((s) => s.signOutAdmin);
  const navigate = useNavigate();
  const name = admin?.username ?? admin?.email ?? 'Admin';

  const visibleNav = nav.filter((item) => canAccessAdminArea(admin, item));

  return (
    <div className="grid min-h-dvh grid-cols-[240px_1fr]">
      <aside className="flex flex-col border-e border-(--color-border) bg-(--color-card)">
        <div className="flex h-16 items-center border-b border-(--color-border) px-5">
          <Link to={ROUTES.admin} className="flex items-center gap-2" aria-label="Tally admin home">
            <Logo />
            <span className="rounded-full border border-(--color-border) bg-(--color-muted) px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-(--color-muted-foreground)">
              Admin
            </span>
          </Link>
        </div>
        <nav className="flex-1 px-3 py-4" aria-label="Admin">
          <p className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-(--color-muted-foreground)/70">
            Operations
          </p>
          <ul className="space-y-0.5">
            {visibleNav.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === ROUTES.admin}
                  className={({ isActive }) =>
                    cn(
                      'relative flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                      'text-(--color-muted-foreground) hover:bg-(--color-muted) hover:text-(--color-foreground)',
                      isActive &&
                        'bg-(--color-accent) text-(--color-primary) before:absolute before:inset-y-1.5 before:start-0 before:w-0.5 before:rounded-full before:bg-(--color-primary)',
                    )
                  }
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      <div className="flex min-h-dvh flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-(--color-border) bg-(--color-background)/90 px-4 backdrop-blur-md sm:px-6 lg:px-8">
          <div className="relative w-full max-w-md">
            <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-(--color-muted-foreground)" />
            <input
              type="search"
              placeholder="Search users, cases…"
              className="h-10 w-full rounded-md border border-(--color-input) bg-(--color-background) ps-9 pe-3 text-sm shadow-sm placeholder:text-(--color-muted-foreground) focus-visible:ring-2 focus-visible:ring-(--color-ring) focus-visible:outline-none"
            />
          </div>

          <div className="ms-auto flex items-center gap-1">
            <ThemeToggle />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-full p-1 transition-colors hover:bg-(--color-muted) focus-visible:ring-2 focus-visible:ring-(--color-ring) focus-visible:outline-none">
                <Avatar size="sm">
                  <AvatarFallback name={name} />
                </Avatar>
                <ChevronDown className="me-1 h-4 w-4 text-(--color-muted-foreground)" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="space-y-0.5">
                  <p className="text-sm font-medium text-(--color-foreground)">{name}</p>
                  <p className="truncate text-xs font-normal tracking-normal text-(--color-muted-foreground) normal-case">
                    {admin?.email}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => {
                  signOutAdmin();
                  navigate(ROUTES.adminLogin, { replace: true });
                }}
              >
                <LogOut className="h-4 w-4" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>

        <main id="main-content" className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <div className="animate-in mx-auto max-w-7xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
